/**
 * Breadcrumbs Component
 *
 * Provides visual navigation breadcrumbs with schema.org BreadcrumbList markup
 * for SEO and improved user experience.
 *
 * @example
 * ```tsx
 * <Breadcrumbs items={[
 *   { name: 'Home', url: '/', position: 1 },
 *   { name: 'Bills', url: '/family-court-reform-bills', position: 2 },
 *   { name: 'Texas', url: '/bill/texas', position: 3 },
 *   { name: 'Three Strikes Law', url: '/bill/texas/three-strikes', position: 4 }
 * ]} />
 * ```
 */

import React from 'react';
import { Breadcrumbs as MuiBreadcrumbs, Link, Typography, Box } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Helmet } from 'react-helmet-async';
import { generateBreadcrumbSchema } from '../utils/schemaGenerators';
import type { BreadcrumbItem } from '../types/seo';

interface BreadcrumbsProps {
  /**
   * Array of breadcrumb items to display
   * Last item in array is considered the current page (not clickable)
   */
  items: BreadcrumbItem[];

  /**
   * Optional aria-label for accessibility
   * @default "breadcrumb navigation"
   */
  ariaLabel?: string;
}

/**
 * Breadcrumbs navigation component with structured data
 *
 * Features:
 * - Visual breadcrumb trail for navigation
 * - schema.org BreadcrumbList JSON-LD for SEO
 * - Last item rendered as non-clickable current page
 * - Responsive styling matching site theme
 */
const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items, ariaLabel = 'breadcrumb navigation' }) => {
  // Don't render if less than 2 items (no breadcrumb trail needed)
  if (items.length < 2) {
    return null;
  }

  // Generate schema.org markup
  const breadcrumbSchema = generateBreadcrumbSchema(items);

  // Last item is current page (not clickable)
  const lastIndex = items.length - 1;

  return (
    <>
      {/* Inject BreadcrumbList schema into page head */}
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Visual breadcrumb navigation */}
      <Box sx={{ py: 2, px: { xs: 0, md: 0 } }}>
        <MuiBreadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label={ariaLabel}
          sx={{
            '& .MuiBreadcrumbs-separator': {
              mx: 0.5,
            },
          }}
        >
          {items.map((item, index) => {
            const isLast = index === lastIndex;

            // Last item: render as non-clickable text
            if (isLast) {
              return (
                <Typography
                  key={item.position}
                  color="text.primary"
                  sx={{
                    fontSize: { xs: '0.875rem', md: '1rem' },
                    fontWeight: 500,
                  }}
                >
                  {item.name}
                </Typography>
              );
            }

            // Earlier items: render as clickable links (or plain text if no url)
            if (!item.url) {
              return (
                <Typography
                  key={item.position}
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                >
                  {item.name}
                </Typography>
              );
            }

            return (
              <Link
                key={item.position}
                component={RouterLink}
                to={item.url}
                underline="hover"
                color="inherit"
                sx={{
                  fontSize: { xs: '0.875rem', md: '1rem' },
                  '&:hover': {
                    color: 'primary.main',
                  },
                }}
              >
                {item.name}
              </Link>
            );
          })}
        </MuiBreadcrumbs>
      </Box>
    </>
  );
};

export default Breadcrumbs;
